import type { TAsyncState, TStateCreator } from "../types";
import {
    initialAsyncState,
    pendingAsyncState,
    fulfilledAsyncState,
    rejectedAsyncState,
} from "../types";

/**
 * Segment mapping version entity (matches backend SegmentMappingVersionResponse)
 */
export type TSegmentVersion = {
    id: string;
    dealId: string;
    segmentType: number;
    versionNumber: number;
    sourceType: "upload" | "snapshot";
    storagePath: string | null;
    originalFilename: string | null;
    createdById: string;
    createdByName: string | null;
    mappingsCount: number;
    description: string | null;
    createdAt: string;
};

/**
 * Paginated segment versions response
 */
export type TPaginatedSegmentVersions = {
    items: TSegmentVersion[];
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
};

/**
 * Version upload result (reuses same shape as propcode)
 */
export type TSegmentVersionUploadResult = {
    success: boolean;
    errors: string[];
    warnings: string[];
    versionId: string | null;
    versionNumber: number | null;
    mappingsCount: number;
    storagePath: string | null;
};

/**
 * Version restore result (reuses same shape as propcode)
 */
export type TSegmentVersionRestoreResult = {
    success: boolean;
    errors: string[];
    warnings: string[];
    mappingsDeleted: number;
    mappingsCreated: number;
};

/**
 * Per-segment async state
 */
export type TSegmentVersionState = {
    versionsListState: TAsyncState<TPaginatedSegmentVersions, string>;
    versionUploadState: TAsyncState<TSegmentVersionUploadResult, string>;
    snapshotState: TAsyncState<TSegmentVersionUploadResult, string>;
    restoreState: TAsyncState<TSegmentVersionRestoreResult, string>;
};

const createInitialSegmentState = (): TSegmentVersionState => ({
    versionsListState: initialAsyncState<TPaginatedSegmentVersions, string>(),
    versionUploadState: initialAsyncState<TSegmentVersionUploadResult, string>(),
    snapshotState: initialAsyncState<TSegmentVersionUploadResult, string>(),
    restoreState: initialAsyncState<TSegmentVersionRestoreResult, string>(),
});

/**
 * Segment versions slice state — keyed by segment type
 */
export type TSegmentVersionsState = {
    segmentVersionsState: Record<number, TSegmentVersionState>;
    segmentVersionSelectedDealId: string | null;
};

/**
 * Segment versions slice actions
 */
export type TSegmentVersionsActions = {
    // List versions
    setSegmentVersionsLoading: (segmentType: number) => void;
    setSegmentVersionsList: (segmentType: number, data: TPaginatedSegmentVersions) => void;
    setSegmentVersionsError: (segmentType: number, error: string) => void;
    setSegmentVersionSelectedDealId: (dealId: string | null) => void;

    // Upload version
    setSegmentVersionUploadLoading: (segmentType: number) => void;
    setSegmentVersionUploadSuccess: (
        segmentType: number,
        result: TSegmentVersionUploadResult,
    ) => void;
    setSegmentVersionUploadError: (segmentType: number, error: string) => void;
    resetSegmentVersionUploadState: (segmentType: number) => void;

    // Create snapshot
    setSegmentSnapshotLoading: (segmentType: number) => void;
    setSegmentSnapshotSuccess: (segmentType: number, result: TSegmentVersionUploadResult) => void;
    setSegmentSnapshotError: (segmentType: number, error: string) => void;
    resetSegmentSnapshotState: (segmentType: number) => void;

    // Restore version
    setSegmentRestoreLoading: (segmentType: number) => void;
    setSegmentRestoreSuccess: (segmentType: number, result: TSegmentVersionRestoreResult) => void;
    setSegmentRestoreError: (segmentType: number, error: string) => void;
    resetSegmentRestoreState: (segmentType: number) => void;

    // Reset all
    resetSegmentVersionsState: () => void;
};

/**
 * Complete segment versions slice
 */
export type TSegmentVersionsSlice = TSegmentVersionsState & TSegmentVersionsActions;

/**
 * Helper: get or create segment state for a given type
 */
const getSegmentState = (
    state: TSegmentVersionsState,
    segmentType: number,
): TSegmentVersionState => {
    return state.segmentVersionsState[segmentType] ?? createInitialSegmentState();
};

/**
 * Segment numbers that carry versioned mappings. Mirrors `VALID_SEGMENT_NUMBERS`
 * in `api/v1/endpoints/segment_versions.py`.
 */
const VERSIONED_SEGMENTS = [2, 4, 5, 6, 7] as const;

const createInitialSegmentStates = (): Record<number, TSegmentVersionState> =>
    Object.fromEntries(VERSIONED_SEGMENTS.map((seg) => [seg, createInitialSegmentState()]));

/**
 * Initial state
 */
const initialState: TSegmentVersionsState = {
    segmentVersionsState: createInitialSegmentStates(),
    segmentVersionSelectedDealId: null,
};

/**
 * Create segment versions slice
 */
export const createSliceSegmentVersions = (): TStateCreator<TSegmentVersionsSlice> => {
    return (set) => ({
        ...initialState,

        // List versions
        setSegmentVersionsLoading: (segmentType) =>
            set((state) => {
                const seg = getSegmentState(state, segmentType);
                return {
                    segmentVersionsState: {
                        ...state.segmentVersionsState,
                        [segmentType]: {
                            ...seg,
                            versionsListState: pendingAsyncState<TPaginatedSegmentVersions, string>(
                                seg.versionsListState.data,
                            ),
                        },
                    },
                };
            }),

        setSegmentVersionsList: (segmentType, data) =>
            set((state) => {
                const seg = getSegmentState(state, segmentType);
                return {
                    segmentVersionsState: {
                        ...state.segmentVersionsState,
                        [segmentType]: {
                            ...seg,
                            versionsListState: fulfilledAsyncState<
                                TPaginatedSegmentVersions,
                                string
                            >(data),
                        },
                    },
                };
            }),

        setSegmentVersionsError: (segmentType, error) =>
            set((state) => {
                const seg = getSegmentState(state, segmentType);
                return {
                    segmentVersionsState: {
                        ...state.segmentVersionsState,
                        [segmentType]: {
                            ...seg,
                            versionsListState: rejectedAsyncState<
                                TPaginatedSegmentVersions,
                                string
                            >(error, seg.versionsListState.data),
                        },
                    },
                };
            }),

        setSegmentVersionSelectedDealId: (dealId) =>
            set(() => ({
                segmentVersionSelectedDealId: dealId,
            })),

        // Upload version
        setSegmentVersionUploadLoading: (segmentType) =>
            set((state) => {
                const seg = getSegmentState(state, segmentType);
                return {
                    segmentVersionsState: {
                        ...state.segmentVersionsState,
                        [segmentType]: {
                            ...seg,
                            versionUploadState: pendingAsyncState<
                                TSegmentVersionUploadResult,
                                string
                            >(seg.versionUploadState.data),
                        },
                    },
                };
            }),

        setSegmentVersionUploadSuccess: (segmentType, result) =>
            set((state) => {
                const seg = getSegmentState(state, segmentType);
                return {
                    segmentVersionsState: {
                        ...state.segmentVersionsState,
                        [segmentType]: {
                            ...seg,
                            versionUploadState: fulfilledAsyncState<
                                TSegmentVersionUploadResult,
                                string
                            >(result),
                        },
                    },
                };
            }),

        setSegmentVersionUploadError: (segmentType, error) =>
            set((state) => {
                const seg = getSegmentState(state, segmentType);
                return {
                    segmentVersionsState: {
                        ...state.segmentVersionsState,
                        [segmentType]: {
                            ...seg,
                            versionUploadState: rejectedAsyncState<
                                TSegmentVersionUploadResult,
                                string
                            >(error, seg.versionUploadState.data),
                        },
                    },
                };
            }),

        resetSegmentVersionUploadState: (segmentType) =>
            set((state) => {
                const seg = getSegmentState(state, segmentType);
                return {
                    segmentVersionsState: {
                        ...state.segmentVersionsState,
                        [segmentType]: {
                            ...seg,
                            versionUploadState: initialAsyncState<
                                TSegmentVersionUploadResult,
                                string
                            >(),
                        },
                    },
                };
            }),

        // Create snapshot
        setSegmentSnapshotLoading: (segmentType) =>
            set((state) => {
                const seg = getSegmentState(state, segmentType);
                return {
                    segmentVersionsState: {
                        ...state.segmentVersionsState,
                        [segmentType]: {
                            ...seg,
                            snapshotState: pendingAsyncState<TSegmentVersionUploadResult, string>(
                                seg.snapshotState.data,
                            ),
                        },
                    },
                };
            }),

        setSegmentSnapshotSuccess: (segmentType, result) =>
            set((state) => {
                const seg = getSegmentState(state, segmentType);
                return {
                    segmentVersionsState: {
                        ...state.segmentVersionsState,
                        [segmentType]: {
                            ...seg,
                            snapshotState: fulfilledAsyncState<TSegmentVersionUploadResult, string>(
                                result,
                            ),
                        },
                    },
                };
            }),

        setSegmentSnapshotError: (segmentType, error) =>
            set((state) => {
                const seg = getSegmentState(state, segmentType);
                return {
                    segmentVersionsState: {
                        ...state.segmentVersionsState,
                        [segmentType]: {
                            ...seg,
                            snapshotState: rejectedAsyncState<TSegmentVersionUploadResult, string>(
                                error,
                                seg.snapshotState.data,
                            ),
                        },
                    },
                };
            }),

        resetSegmentSnapshotState: (segmentType) =>
            set((state) => {
                const seg = getSegmentState(state, segmentType);
                return {
                    segmentVersionsState: {
                        ...state.segmentVersionsState,
                        [segmentType]: {
                            ...seg,
                            snapshotState: initialAsyncState<TSegmentVersionUploadResult, string>(),
                        },
                    },
                };
            }),

        // Restore version
        setSegmentRestoreLoading: (segmentType) =>
            set((state) => {
                const seg = getSegmentState(state, segmentType);
                return {
                    segmentVersionsState: {
                        ...state.segmentVersionsState,
                        [segmentType]: {
                            ...seg,
                            restoreState: pendingAsyncState<TSegmentVersionRestoreResult, string>(
                                seg.restoreState.data,
                            ),
                        },
                    },
                };
            }),

        setSegmentRestoreSuccess: (segmentType, result) =>
            set((state) => {
                const seg = getSegmentState(state, segmentType);
                return {
                    segmentVersionsState: {
                        ...state.segmentVersionsState,
                        [segmentType]: {
                            ...seg,
                            restoreState: fulfilledAsyncState<TSegmentVersionRestoreResult, string>(
                                result,
                            ),
                        },
                    },
                };
            }),

        setSegmentRestoreError: (segmentType, error) =>
            set((state) => {
                const seg = getSegmentState(state, segmentType);
                return {
                    segmentVersionsState: {
                        ...state.segmentVersionsState,
                        [segmentType]: {
                            ...seg,
                            restoreState: rejectedAsyncState<TSegmentVersionRestoreResult, string>(
                                error,
                                seg.restoreState.data,
                            ),
                        },
                    },
                };
            }),

        resetSegmentRestoreState: (segmentType) =>
            set((state) => {
                const seg = getSegmentState(state, segmentType);
                return {
                    segmentVersionsState: {
                        ...state.segmentVersionsState,
                        [segmentType]: {
                            ...seg,
                            restoreState: initialAsyncState<TSegmentVersionRestoreResult, string>(),
                        },
                    },
                };
            }),

        // Reset all
        resetSegmentVersionsState: () =>
            set(() => ({
                ...initialState,
                segmentVersionsState: createInitialSegmentStates(),
            })),
    });
};
